import React, { useMemo, useState, type JSX } from 'react';
import arrowDown from '@/assets/svg/static/arrowDown.svg';
import starIcon from '@/assets/svg/static/star.svg';
import {
  EVENTS,
  PROPERTIES,
  trackEvent,
  TRACKING_SOURCES,
} from '@/services/posthog';
import type { ProductReview } from '@/types/Product.types';
import { updatedImageUrl } from '@/utils/formatting';
import { ReviewCardWithTracking } from '../ReviewCardWithTracking';
import { ReviewMediaThumbnail } from '../ReviewMediaThumbnail';
import styles from './ProductReviewSection.module.css';
import type { ProductReviewSectionProps } from './ProductReviewSection.types';

export const ProductReviewSection = React.memo(
  ({
    reviewsData,
    ratingVal,
    ratingCountVal,
    productDetails,
    onReviewImageClick,
  }: ProductReviewSectionProps): JSX.Element => {
    const [showAllReviews, setShowAllReviews] = useState<boolean>(false);

    const { reviewImages, reviewOverlays } = useMemo(() => {
      if (!reviewsData?.reviews) {
        return {
          reviewImages: [],
          reviewOverlays: [],
        };
      }
      const images: string[] = [];
      const overlays: ProductReview[] = [];

      reviewsData.reviews.forEach((review) => {
        if (review.images && review.images.length > 0) {
          review.images.forEach((img) => {
            images.push(updatedImageUrl(img));
            overlays.push(review);
          });
        }
      });

      return {
        reviewImages: images,
        reviewOverlays: overlays,
      };
    }, [reviewsData]);

    if (!reviewsData) return <></>;

    return (
      <div className={styles.reviewsSection}>
        <div className={styles.reviewsHeader}>
          <div className={styles.reviewsHeaderLeft}>
            <span className={styles.reviewsHeaderTitle}>CUSTOMER REVIEWS</span>
            <span className={styles.reviewStars}>
              <span className={styles.ratingText}>
                {Number(ratingVal).toFixed(1)}
              </span>
              <img
                src={starIcon}
                alt="Star"
                className={styles.reviewStarIcon}
              />
            </span>
            <span className={styles.reviewsCount}>
              {ratingCountVal} reviews
            </span>
          </div>
        </div>

        {reviewsData.aiReviewSummary && (
          <div className={styles.reviewsSummary}>
            {reviewsData.aiReviewSummary}
          </div>
        )}

        {reviewImages.length > 0 && (
          <div className={styles.customerPhotosRow}>
            {reviewImages.slice(0, 4).map((imgUrl, index) => {
              const isLast = index === 3 && reviewImages.length > 4;
              const moreCount = reviewImages.length - 3;
              return (
                <ReviewMediaThumbnail
                  key={index}
                  src={imgUrl}
                  alt={`Review photo ${index + 1}`}
                  className={styles.customerPhoto}
                  isLast={isLast}
                  moreCount={moreCount}
                  onClick={() => {
                    trackEvent(EVENTS.PDP_V2_CAROUSEL_IMAGE_CLICK, {
                      [PROPERTIES.PRODUCT_ID]: productDetails.id,
                      [PROPERTIES.IMAGE_INDEX]: index,
                      [PROPERTIES.IMAGE_URL]: imgUrl,
                      [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
                    });
                    onReviewImageClick(reviewImages, index, reviewOverlays);
                  }}
                />
              );
            })}
          </div>
        )}

        {reviewsData.reviews
          .slice(0, showAllReviews ? reviewsData.reviews.length : 2)
          .map((review, idx) => (
            <ReviewCardWithTracking
              key={idx}
              review={review}
              productId={productDetails.id}
              onReviewImageClick={onReviewImageClick}
            />
          ))}

        {!showAllReviews && reviewsData.reviews.length > 2 && (
          <button
            className={styles.viewMoreReviewsBtn}
            onClick={() => {
              trackEvent(EVENTS.PDP_V2_VIEW_MORE_REVIEWS_CLICK, {
                [PROPERTIES.PRODUCT_ID]: productDetails.id,
                [PROPERTIES.TOTAL_REVIEWS]: reviewsData.reviews.length,
                [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
              });
              setShowAllReviews(true);
            }}
          >
            <span>VIEW MORE REVIEWS</span>
            <img
              src={arrowDown}
              className={styles.viewMoreChevron}
              alt="Chevron Down"
            />
          </button>
        )}
      </div>
    );
  }
);

export default ProductReviewSection;
